import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Avatar,
  Button,
  Dropdown,
  Flex,
  Image,
  Input,
  Popconfirm,
  Space,
  Table,
  Tooltip,
  Typography,
} from "antd";
import axiosInstance from "../../services/axios";
import { get, isEmpty, pick } from "lodash";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { createStyles } from "antd-style";
import Filter from "./filter";
import { useBoolean } from "hooks/use-boolean";
import LateInfoDropDown from "./components/LateInfoDropDown";
import {
  DownloadOutlined,
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { getInOfficeHours } from "utils/functions";
import ScheduleDropdown from "./components/ScheduleDropDown";
import { IoBookmarkSharp } from "react-icons/io5";

const overAllDaysKeys = [
  { key: "totalScheduledDays", title: "Kelish kerak bo'lgan kunlar" },
  { key: "actualAttendanceDays", title: "Necha marta keldi" },
  { key: "noProblemDays", title: "Muammosiz kunlar" },
  // { key: "totalMissingHours", title: "Ishlamagan soat" },
];

const useStyle = createStyles(({ css }) => {
  return {
    customTable: css`
      .ant-table {
        .ant-table-container {
          .ant-table-body,
          .ant-table-content {
            scrollbar-width: thin;
            scrollbar-color: #eaeaea transparent;
            scrollbar-gutter: stable;
          }
        }

        .has-late-info {
          position: relative;
          overflow: hidden;
          svg {
            position: absolute;
            top: -2px;
            right: 5px;
            filter: drop-shadow(2px 2px 4px rgba(54, 54, 54, 0.5));
          }
        }
      }
    `,
  };
});

interface IFilter {
  year?: number;
  month?: number;
  departmentIds?: number[];
  day?: number;
  types?: string[];
}

interface Props {
  permissions: Record<string, boolean>;
}

// departmentIds: [35], month: 11, year: 2024

const WorkingHoursTab = ({ permissions }: Props) => {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<Partial<IFilter>>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [searchParams] = useSearchParams();
  const { styles } = useStyle();
  const openUserPhoto = useBoolean();
  const dropDown = useBoolean();
  const downloading = useBoolean();
  const isAdmin = get(permissions, "canChangeWorkDayReport");
  const canReadManager = get(permissions, "canReadManager");

  const { data: formData } = useQuery({
    queryKey: ["working-hours-list-form"],
    queryFn: async () => {
      axiosInstance.defaults.headers.common.Authorization =
        searchParams.get("token");
      return await axiosInstance.get("/turniket/v1/user-work-day/form");
    },
  });

  const { data: workingHoursData, isFetching } = useQuery({
    queryKey: ["working-hours-list", filter],
    queryFn: async () => {
      axiosInstance.defaults.headers.common.Authorization =
        searchParams.get("token");
      return await axiosInstance.post("/turniket/v1/user-work-day", filter);
    },
    enabled: !!filter.year && !!filter.month,
  });

  const { mutate: closeReport, isPending: isClosePending } = useMutation({
    mutationKey: ["/turniket/v1/user-work-day/close", filter],
    mutationFn: async () => {
      axiosInstance.defaults.headers.common.Authorization =
        searchParams.get("token");
      return await axiosInstance.post(
        "/turniket/v1/user-work-day/close",
        pick(filter, "year", "month")
      );
    },
    onSuccess: () => refetchQuery(),
  });

  const downloadExcel = useCallback(async () => {
    try {
      downloading.onTrue();

      const token = searchParams.get("token");
      axiosInstance.defaults.headers.common.Authorization = token;

      const { year, month, departmentIds, day, types } = filter;
      const params = new URLSearchParams({
        AUTH_ID: token || "",
        ...(year && { year: year.toString() }),
        ...(month && { month: month.toString() }),
        ...(day &&
          month && {
            date: `${year}-${month.toString().padStart(2, "0")}-${day
              .toString()
              .padStart(2, "0")}`,
          }),
        ...(departmentIds?.length && {
          departmentIds: departmentIds.join(","),
        }),
        ...(types?.length && { lateTypes: types.join(",") }),
      });

      const response = await axiosInstance.get(
        `/turniket/v1/user-work-day/download-excel?${params.toString()}`,
        { responseType: "blob" }
      );

      downloading.onFalse();

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "working-hours.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      downloading.onFalse();
    }
  }, [downloading, filter, searchParams]);

  const usersList: any[] = useMemo(() => {
    const dateMap = get(workingHoursData, "data.data.dateMap", []);
    let dates = get(workingHoursData, "data.data.dates", []);
    const tempUsers = get(workingHoursData, "data.data.users", []);

    const hasAbsent = filter.types?.includes("ABSENT");

    if (filter.day) {
      dates = dates.filter((date: string) =>
        date.startsWith(String(filter.day).padStart(2, "0"))
      );
    }

    tempUsers.forEach((user: any) => {
      user.hidden = !isEmpty(filter.types);

      dates.forEach((date: string) => {
        const key = getKey(user.id, date);
        const record = dateMap[key];

        if (!record && hasAbsent) {
          user.hidden = false;
        }

        if (
          user.hidden &&
          filter.types &&
          !isEmpty(get(record, "lateInfoList")) &&
          hasThisTypes(filter.types, get(record, "lateInfoList", []))
        ) {
          user.hidden = false;
        }
      });
    });

    return tempUsers.filter(
      ({ hidden, name, lastName }: any) =>
        !hidden &&
        [name, lastName].some((n) =>
          n.toLowerCase().startsWith(searchTerm.toLowerCase())
        )
    );
  }, [workingHoursData, filter.types, filter.day, searchTerm]);

  const columns = useMemo(() => {
    const cols: any = [
      {
        title: "№",
        dataIndex: "no",
        render: (_: any, i: any, index: number) => index + 1,
        width: 30,
        fixed: "left",
      },
      {
        title: "Photo",
        dataIndex: "name",
        key: "name",
        fixed: "left",
        render: (firstName: string, record: any) => (
          <Space>
            <Tooltip title={firstName} placement="right">
              <Avatar
                style={{ cursor: "pointer" }}
                icon={<UserOutlined />}
                onClick={() => openUserPhoto.onTrue(get(record, "photo"))}
                src={get(record, "photo")}
                alt="Avatar"
              />
            </Tooltip>
          </Space>
        ),
      },
      {
        title: "First name",
        dataIndex: "name",
        key: "name",
        width: 200,
        render: (name: any, record: any) => (
          <a
            href={`https://space.pdp.uz/company/personal/user/${get(
              record,
              "id"
            )}/`}
            target="_blank"
            rel="noreferrer"
          >
            {name}
          </a>
        ),
      },
      {
        title: "Last name",
        dataIndex: "lastName",
        key: "lastName",
        width: 150,
      },
      {
        title: "Schedule",
        dataIndex: "schedule",
        key: "schedule",
        onCell: (record: any) => ({
          style: { maxWidth: "150px", cursor: "pointer" },
          onClick: (e: any) => {
            const rect = e.target?.getBoundingClientRect();
            dropDown.onTrue({
              x: rect.x,
              y: rect.y - 40,
              schedule: get(record, "schedule"),
            });
          },
        }),
        ellipsis: { showTitle: false },
        render: (schedule: Record<string, any>) => get(schedule, "name"),
        width: 150,
      },
    ];

    const dateMap = get(workingHoursData, "data.data.dateMap", []);
    let dates = get(workingHoursData, "data.data.dates", []);
    const hasAbsent = filter.types?.includes("ABSENT");

    if (filter.day) {
      dates = dates.filter((date: string) =>
        date.startsWith(String(filter.day).padStart(2, "0"))
      );
    }

    if (!isEmpty(filter.types)) {
      const dateMapEntries = Object.entries(dateMap);

      dates = dates.filter((date: string) => {
        if (hasAbsent) {
          let showColumn = true;
          for (const user of usersList) {
            const key = getKey(user.id, date);
            if (dateMap[key]) {
              showColumn = false;
            }
          }
          return showColumn;
        }

        for (const map of dateMapEntries) {
          if (map[0].includes(date) && filter.types) {
            if (hasThisTypes(filter.types, get(map, "[1].lateInfoList", [])))
              return true;
          }
        }

        return false;
      });
    }

    dates.forEach((date: string) => {
      cols.push({
        title: (
          <Flex justify="center" align="center">
            {date.replace(/\.\d{4}/, "")}
          </Flex>
        ),
        dataIndex: date,
        key: date,
        width: 130,
        className: "date-field",
        onCell: (record: any) => {
          const key = getKey(record.id, date);
          const recordData = dateMap[key];
          const lateInfoList = recordData?.lateInfoList;
          let isOutOfFilter = false;
          let bgColor = recordData?.cellColor;
          const hasAbsent = filter.types?.includes("ABSENT");

          if (!isEmpty(filter.types)) {
            let match = false;

            if (hasAbsent && (!recordData || recordData.penalty)) match = true;
            else
              lateInfoList?.forEach((item: any) => {
                if (filter.types?.includes(item.type)) {
                  match = true;
                  return;
                }
              });

            if (!match) isOutOfFilter = true;
          }

          return {
            onClick: (e: any) => {
              if (!recordData || isOutOfFilter) return;
              const rect = e.target?.getBoundingClientRect();
              dropDown.onTrue({
                x: rect.x,
                y: rect.y - 40,
                ...recordData,
                user: record,
              });
            },
            style: {
              backgroundColor:
                bgColor ??
                (record?.schedule?.days.some((day: any) =>
                  day.day.toLowerCase().includes(date.slice(-3).toLowerCase())
                ) &&
                !record?.schedule?.holidays.includes(
                  date
                    .slice(0, -4)
                    .replace(/\./g, "-")
                    .split("-")
                    .reverse()
                    .join("-")
                )
                  ? "#FC5C65"
                  : ""),
              color: "white",
              cursor: recordData ? "pointer" : "",
              textAlign: "center",
              userSelect: "none",
            },
            className:
              (lateInfoList?.length || recordData?.temporaryBreaks?.length) &&
              !isOutOfFilter &&
              "has-late-info",
          };
        },
        render: (_: any, record: any) => {
          const key = getKey(record.id, date);
          const data = dateMap[key];
          if (!data) {
            const exactDay = record?.schedule?.days.find((day: any) =>
              day?.day.toLowerCase().includes(date.slice(-3).toLowerCase())
            );

            const debtHours = (exactDay?.workingHour / 3600).toString();

            return `00:00 (${debtHours.substring(
              0,
              debtHours.indexOf(".") + 2
            )})`;
          }

          const lateInfoList: any[] = data.lateInfoList ?? [];
          const hasLateOrBreak =
            lateInfoList.length > 0 || (data.temporaryBreaks?.length ?? 0) > 0;

          const isOutOfFilter = false; // TODO: real condition?

          return (
            <>
              {getInOfficeHours(data.seconds)}
              {data.leftTime > 0 && ` (${data.leftTime})`}
              {hasLateOrBreak && !isOutOfFilter && (
                <IoBookmarkSharp
                  size={20}
                  color={
                    lateInfoList.some(({ ignore }) => !ignore)
                      ? "#ffee00"
                      : "#fff"
                  }
                />
              )}
            </>
          );
        },
      });
    });

    if (!filter.day && isEmpty(filter.types)) {
      cols.push({
        title: <Flex justify="center">Days</Flex>,
        dataIndex: "overallDays",
        key: "overallDays",
        render: (overallDays: Record<string, any>) =>
          overallDays && (
            <Flex justify="space-evenly">
              {overAllDaysKeys.map(({ key, title }, index) => (
                <>
                  <Tooltip title={title}>
                    <span className="hover:text-green-600 cursor-pointer">
                      {get(overallDays, key)}
                    </span>
                  </Tooltip>
                  {index < 2 && "  -  "}
                </>
              ))}
            </Flex>
          ),
      });
    }

    cols.push({
      title: (
        <Tooltip title={""}>
          <Flex justify="center">⏰</Flex>
        </Tooltip>
      ),
      dataIndex: "overallDays",
      key: "overallDays",
      innerWidth: "max-width",
      render: (overallDays: Record<string, any>) =>
        overallDays && (
          <Tooltip
            title={"Kelgan kunlarda ishlamagan soatlar"}
            placement="left"
          >
            <Flex
              justify="center"
              className="hover:text-green-600 cursor-pointer"
            >
              {overallDays.totalMissingHours}
            </Flex>
          </Tooltip>
        ),
    });

    return cols;
  }, [
    workingHoursData,
    filter.types,
    filter.day,
    openUserPhoto,
    usersList,
    dropDown,
  ]);

  const refetchQuery = () =>
    queryClient.invalidateQueries({ queryKey: ["working-hours-list", filter] });

  useEffect(() => {
    if (!isAdmin) {
      setFilter({
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
      });
    }
  }, [isAdmin]);

  const inputSearchHandler = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(e.currentTarget.value);
  };

  return (
    <div className="excel-like-table">
      <Flex align="center" justify="space-between" className="my-3">
        <Flex className="w-full">
          <Typography.Title className="mt-0" level={4}>
            Working hours
          </Typography.Title>
        </Flex>

        <Flex className="w-[40%] bg-red" gap={10}>
          <Input
            placeholder="Search"
            disabled={isEmpty(filter)}
            prefix={<SearchOutlined />}
            onChange={inputSearchHandler}
          />
          <Filter
            isEmpty={isEmpty(filter)}
            isAdmin={isAdmin}
            departments={get(formData, "data.data.departments", [])}
            onFilterChange={setFilter}
          />
        </Flex>

        <Flex justify="end" className="w-full" gap={10}>
          {get(permissions, "canCloseWorkDayReport") && filter.month && (
            <Popconfirm
              okButtonProps={{ loading: isClosePending }}
              onConfirm={() => closeReport()}
              placement="leftTop"
              title={"Are you sure to close this month's report?"}
              description={"Close this month's report"}
              okText="Yes"
              cancelText="No"
            >
              <Button color="danger" variant="filled">
                Close month's report
              </Button>
            </Popconfirm>
          )}
          <Button
            loading={downloading.value}
            onClick={downloadExcel}
            color="primary"
            variant="filled"
            icon={<DownloadOutlined />}
          >
            Download excel
          </Button>
        </Flex>
      </Flex>

      <Table
        size="small"
        bordered
        className={styles.customTable}
        scroll={{ x: "max-content", y: 40 * 20 }}
        loading={isFetching}
        rowKey={"id"}
        pagination={false}
        dataSource={usersList}
        columns={columns}
      />

      <Image
        width={200}
        style={{ display: "none" }}
        src={openUserPhoto.data}
        preview={{
          visible: openUserPhoto.value,
          scaleStep: 0.5,
          src: openUserPhoto.data,
          onVisibleChange: openUserPhoto.onToggle,
        }}
      />

      <Dropdown
        overlayStyle={{ zIndex: 1 }}
        dropdownRender={() => {
          if (get(dropDown, "data.schedule"))
            return (
              <ScheduleDropdown schedule={get(dropDown, "data.schedule")} />
            );
          return (
            <LateInfoDropDown
              isAdmin={canReadManager}
              refetchQuery={refetchQuery}
              usersMap={get(formData, "data.data.users", {})}
              data={dropDown.data}
            />
          );
        }}
        open={dropDown.value}
        getPopupContainer={() => document.body}
        onOpenChange={dropDown.onToggle}
        placement="bottomRight"
        trigger={["click"]}
        //
      >
        <div
          style={{
            position: "absolute",
            top: get(dropDown, "data.y"),
            left: get(dropDown, "data.x"),
            visibility: "hidden",
          }}
        />
      </Dropdown>
    </div>
  );
};

const hasThisTypes = (types: string[], lateInfoList: any[]) => {
  return lateInfoList.some((item) => types.includes(item.type));
};

const getKey = (userId: string, date: string) => `${userId}:${date}`;

export default WorkingHoursTab;
